import { DateTime } from "luxon";
import { z } from "zod";

import { parseRelationshipToSubscriber, RelationshipToSubscriber } from "@/shared/models/coverage";
import { normalizeInsuranceMemberId } from "@/shared/models/insurance-evidence";
import { eligibilityRequestMemberId } from "@/src/lib/blocks/eligibility-check-inputs";
import { isSameDayEligibilityCheck } from "@/src/lib/insurance/eligibility-freshness";
import type { Coverage } from "@/src/models/coverage";
import type { Group } from "@/src/models/group";
import type { Patient } from "@/src/models/patient";
import { PersonGender } from "@/src/models/person";
import { getEligibilityCoveragePosition, type StediEligibilityCheck } from "@/src/models/stedi-eligibility-check";
import { StediEligibilityStatusValue } from "@/src/models/stedi-eligibility-status";

const personSchema = z.object({
  firstName: z.string().trim().min(1),
  lastName: z.string().trim().min(1),
  dateOfBirth: z.string().optional(),
});

export const stediSubscriberSchema = personSchema.extend({ memberId: z.string().trim().min(1) });

export const coverageSubscriberSchema = z.object({
  coverageId: z.string(),
  order: z.number().nullable(),
  name: z.string().nullable(),
  dateOfBirth: z.string().nullable(),
  memberId: z.string().nullable(),
  patientRelationship: z.string().nullable(),
  differsFromPatient: z.boolean().nullable(),
  reviewReason: z.string().nullable(),
});
export type CoverageSubscriber = z.infer<typeof coverageSubscriberSchema>;

const writableSubscriberRelationships = new Map<string, RelationshipToSubscriber>([
  ["spouse", RelationshipToSubscriber.SPOUSE],
  ["child", RelationshipToSubscriber.CHILD],
  ["other", RelationshipToSubscriber.OTHER],
  ["other relationship", RelationshipToSubscriber.OTHER],
]);

export function stediCoverageSubscriber(
  coverage: Coverage,
  patient: Patient,
  check: StediEligibilityCheck | null
): CoverageSubscriber {
  const empty: CoverageSubscriber = {
    coverageId: coverage.id,
    order: coverage.order,
    name: null,
    dateOfBirth: null,
    memberId: null,
    patientRelationship: null,
    differsFromPatient: null,
    reviewReason: "Stedi did not return subscriber details for this coverage.",
  };
  const request = z
    .object({ tradingPartnerServiceId: z.string().min(1), subscriber: stediSubscriberSchema })
    .safeParse(check?.requestJson);
  const response = z
    .object({
      subscriber: stediSubscriberSchema.partial({ memberId: true }),
      dependents: z
        .array(
          personSchema.partial().extend({
            firstName: z.string().trim().optional(),
            lastName: z.string().trim().optional(),
            relationToSubscriber: z.string().optional(),
          })
        )
        .optional(),
      errors: z.array(z.unknown()).optional(),
    })
    .safeParse(check?.responseJson);

  if (
    !check ||
    check.deletedAt ||
    check.checkFailed !== false ||
    check.groupId !== coverage.groupId ||
    check.groupId !== patient.groupId ||
    check.patientId !== patient.id ||
    (coverage.patientId && coverage.patientId !== patient.id) ||
    check.coveragePosition !== getEligibilityCoveragePosition(coverage) ||
    !request.success ||
    !response.success ||
    response.data.errors?.length
  )
    return empty;

  // A home-plan retry saves the final request's payer, not proof of the original routing.
  if (request.data.tradingPartnerServiceId !== coverage.customerPayer)
    return {
      ...empty,
      reviewReason:
        "Stedi returned subscriber details under a different payer. Confirm the payer and policy before using those details; they are withheld here pending review.",
    };

  const memberId = normalizeInsuranceMemberId(
    eligibilityRequestMemberId(request.data.tradingPartnerServiceId, coverage.subscriberId ?? "")
  );
  const subscriber = response.data.subscriber;

  if (
    !memberId ||
    normalizeInsuranceMemberId(request.data.subscriber.memberId) !== memberId ||
    (subscriber.memberId !== undefined && normalizeInsuranceMemberId(subscriber.memberId) !== memberId)
  )
    return empty;

  const matchesPatient = (person: Partial<z.infer<typeof personSchema>>) =>
    !!person.firstName &&
    !!person.lastName &&
    person.firstName.toUpperCase() === patient.givenName?.trim().toUpperCase() &&
    person.lastName.toUpperCase() === patient.familyName?.trim().toUpperCase() &&
    !!patient.dateOfBirth &&
    person.dateOfBirth === patient.dateOfBirth.replaceAll("-", "");
  const isPatient = matchesPatient(subscriber);
  const dependents = response.data.dependents?.filter(matchesPatient) ?? [];
  const dependent = dependents.length === 1 ? dependents[0] : null;
  const dob = subscriber.dateOfBirth ? DateTime.fromFormat(subscriber.dateOfBirth, "yyyyMMdd", { zone: "UTC" }) : null;
  const patientRelationship = isPatient ? RelationshipToSubscriber.SELF : (dependent?.relationToSubscriber ?? null);
  const relationship = parseRelationshipToSubscriber(patientRelationship);

  return {
    ...empty,
    name: `${subscriber.firstName} ${subscriber.lastName}`,
    dateOfBirth: dob?.isValid ? dob.toISODate() : null,
    memberId: subscriber.memberId ?? null,
    patientRelationship,
    differsFromPatient: isPatient ? false : dependent && dob?.isValid ? true : null,
    reviewReason:
      !isPatient && !dependent
        ? "Confirm the subscriber: Stedi did not identify this patient in its returned subscriber or dependent details."
        : relationship && coverage.relationshipToSubscriber && coverage.relationshipToSubscriber !== relationship
          ? "Stedi's patient relationship differs from the referral; confirm the relationship to the subscriber."
          : !subscriber.memberId
            ? "Stedi did not return a subscriber member ID."
            : !dob?.isValid
              ? "Stedi did not return a valid subscriber date of birth."
              : !isPatient && !dependent?.relationToSubscriber
                ? "Confirm the patient's relationship to the subscriber."
                : null,
  };
}

/** Resolve non-self policyholder fields for a write using current referral eligibility evidence. */
export function stediSubscriberCoverage(
  coverage: Coverage,
  patient: Patient,
  group: Group,
  referralId: string | null,
  check: StediEligibilityCheck | null,
  now: string
): Coverage {
  const summary = stediCoverageSubscriber(coverage, patient, check);

  if (coverage.relationshipToSubscriber === RelationshipToSubscriber.SELF && summary.differsFromPatient !== true)
    return coverage;
  if (
    !referralId ||
    !check ||
    check.referralId !== referralId ||
    check.groupId !== group.id ||
    check.eligibilityStatus !== StediEligibilityStatusValue.ACTIVE ||
    check.tradingPartnerServiceId !== coverage.customerPayer ||
    !isSameDayEligibilityCheck(check.createdAt, group.timezone, now) ||
    summary.reviewReason ||
    summary.differsFromPatient !== true ||
    !summary.dateOfBirth ||
    summary.dateOfBirth > DateTime.fromISO(now, { zone: group.timezone }).toISODate()!
  )
    throw new Error("Insurance subscriber needs review: obtain a current matching Stedi check before syncing.");
  const subscriber = z
    .object({ subscriber: stediSubscriberSchema.extend({ gender: z.string().optional() }) })
    .parse(check.responseJson).subscriber;
  const relationship = writableSubscriberRelationships.get(summary.patientRelationship?.trim().toLowerCase() ?? "");
  const sameName = (left: string | null | undefined, right: string) =>
    left?.trim().toUpperCase() === right.toUpperCase();
  const request = z
    .object({ subscriber: personSchema, dependents: z.array(personSchema).optional() })
    .parse(check.requestJson);
  const requestedPeople = request.dependents?.length ? request.dependents : [request.subscriber];

  if (
    !relationship ||
    !requestedPeople.some(
      (person) =>
        sameName(patient.givenName, person.firstName) &&
        sameName(patient.familyName, person.lastName) &&
        person.dateOfBirth === patient.dateOfBirth?.replaceAll("-", "")
    ) ||
    (coverage.subscriberGivenName && !sameName(coverage.subscriberGivenName, subscriber.firstName)) ||
    (coverage.subscriberFamilyName && !sameName(coverage.subscriberFamilyName, subscriber.lastName)) ||
    (coverage.subscriberDateOfBirth && coverage.subscriberDateOfBirth !== summary.dateOfBirth)
  )
    throw new Error(
      "Insurance subscriber identity or relationship conflicts with the referral; review before syncing."
    );

  return {
    ...coverage,
    subscriberGivenName: subscriber.firstName,
    subscriberFamilyName: subscriber.lastName,
    subscriberDateOfBirth: summary.dateOfBirth,
    subscriberSex:
      coverage.subscriberSex ??
      (subscriber.gender === "M" ? PersonGender.MALE : subscriber.gender === "F" ? PersonGender.FEMALE : null),
    relationshipToSubscriber: relationship,
  };
}
